import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  PatientAction,
  PatientActionCheckIn,
  PatientActionCheckInCreate,
  PatientActionCreate,
} from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type CustomerActionsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; actions: PatientAction[]; checkIns: PatientActionCheckIn[] };

export interface CustomerActions {
  state: CustomerActionsState;
  actions: PatientAction[];
  checkIns: PatientActionCheckIn[];
  add: (input: PatientActionCreate) => Promise<PatientAction>;
  addCheckIn: (actionId: string, input: PatientActionCheckInCreate) => Promise<PatientActionCheckIn>;
}

// One audited read per open; a mutation appends the POST result locally
// instead of re-reading, because every GET is its own patient_actions
// disclosure. Oldest first, as the route returns them.
export function useCustomerActions(id: string | undefined): CustomerActions {
  const api = useApi();
  const [state, setState] = useState<CustomerActionsState>({ status: "loading" });

  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    api.patients
      .actions(id)
      .then((res) => {
        if (!cancelled) {
          setState({ status: "success", actions: res.actions, checkIns: res.checkIns });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load actions" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const add = useCallback(
    async (input: PatientActionCreate) => {
      if (!id) {
        throw new Error("No patient");
      }
      const action = await api.patients.addAction(id, input);
      setState((current) =>
        current.status === "success" ? { ...current, actions: [...current.actions, action] } : current,
      );
      return action;
    },
    [api, id],
  );

  const addCheckIn = useCallback(
    async (actionId: string, input: PatientActionCheckInCreate) => {
      if (!id) {
        throw new Error("No patient");
      }
      const checkIn = await api.patients.addActionCheckIn(id, actionId, input);
      setState((current) =>
        current.status === "success" ? { ...current, checkIns: [...current.checkIns, checkIn] } : current,
      );
      return checkIn;
    },
    [api, id],
  );

  return useMemo(
    () => ({
      state,
      actions: state.status === "success" ? state.actions : [],
      checkIns: state.status === "success" ? state.checkIns : [],
      add,
      addCheckIn,
    }),
    [state, add, addCheckIn],
  );
}
